import {
  CopyX,
  FileText,
  FlaskConical,
  GraduationCap,
  HeartPulse,
  LayoutGrid,
  LocateFixed,
  Microscope,
  Presentation,
  Scroll,
  Sparkles,
  Split,
  SquarePen,
  TextSearch,
  Trophy,
} from 'lucide-react';

import type { PageContext } from '@/types/pageContext';

import type { Shortcut } from './shortcutsConfig';

export const PAGE_SHORTCUTS: Partial<
  Record<PageContext['pageType'], Shortcut[]>
> = {
  product: [
    {
      id: 'recommend-experiments',
      order: 1,
      icon: FlaskConical,
      label: 'Suggest experiments to validate key risks',
      prompt:
        'Based on this product\u2019s key assumptions and context sources, suggest 2\u20133 concrete experiments I could run to de-risk the biggest unknowns.',
      condition: 'health-score-gte-30',
    },
    {
      id: 'summarize-product',
      order: 2,
      icon: Presentation,
      label: 'Summarize this product for stakeholders',
      prompt:
        'Draft a short product status summary I can paste into an email or Slack message \u2014 cover what it does, current state, and what\u2019s next.',
      condition: 'health-score-gte-30',
    },
    {
      id: 'explain-maturity',
      order: 3,
      icon: HeartPulse,
      label: 'Explain my Product Health Score',
      prompt:
        'Break down my Product Health Score \u2014 explain what\u2019s driving it, what\u2019s pulling it down, and give me 2\u20133 specific actions to improve it.',
      condition: 'low-maturity',
    },
  ],
  'tools-list': [
    {
      id: 'explain-categories',
      order: 1,
      icon: LayoutGrid,
      label: 'Explain what each tool category is for',
      prompt:
        'Explain the purpose of each tool category on this page in plain language, tailored to my product context.',
      condition: 'first-visit',
    },
    {
      id: 'recommend-tool',
      order: 2,
      icon: Microscope,
      label: 'Suggest the best tool to start discovery',
      prompt:
        'Based on my product context, recommend 1\u20132 tools to start customer discovery and explain why they fit.',
      condition: 'first-visit',
    },
  ],
  'tool-versions-list': [
    {
      id: 'unlinked-versions',
      order: 1,
      icon: CopyX,
      label: 'Highlight tool versions not linked to any product',
      prompt:
        'Find any tool versions that aren\u2019t linked to an active product and suggest what I should do with each one \u2014 archive, link, or update.',
      condition: 'multiple-versions',
    },
  ],
  'experiments-list': [
    {
      id: 'no-experiments',
      order: 1,
      icon: TextSearch,
      label: 'Identify hypotheses without experiments',
      prompt:
        'Scan all my hypotheses and surface the ones that aren\u2019t linked to any experiments yet \u2014 group them by priority and the assumptions they test.',
      condition: 'has-hypotheses',
    },
    {
      id: 'stuck-drafts',
      order: 2,
      icon: LocateFixed,
      label: 'Spot experiments stuck in draft',
      prompt:
        'Flag any experiments that have been sitting in Draft status for a while and suggest what I should do to get them moving.',
      condition: 'has-hypotheses',
    },
    {
      id: 'testing-progress',
      order: 3,
      icon: Presentation,
      label: 'Summarize my experiment progress',
      prompt:
        'Summarize progress across all my hypotheses and experiments \u2014 what\u2019s running, what\u2019s been learned, and what\u2019s still pending \u2014 in a short update I can share.',
      condition: 'has-hypotheses',
    },
    {
      id: 'explain-difference',
      order: 4,
      icon: GraduationCap,
      label: "What's the difference: hypothesis vs. experiment?",
      prompt:
        'Explain the difference between a hypothesis and an experiment in plain language, with examples from my current product context.',
      condition: 'no-hypotheses',
    },
  ],
  hypothesis: [
    {
      id: 'rewrite',
      order: 1,
      icon: SquarePen,
      label: 'Sharpen this hypothesis',
      prompt:
        'Rewrite this hypothesis into a clean "We believe that [action] will cause [result] because [rationale]" format so it\u2019s easier to test.',
      condition: 'always',
    },
    {
      id: 'break-down',
      order: 2,
      icon: Split,
      label: 'Split this into sub-hypotheses',
      prompt:
        'Check if this hypothesis is trying to prove too many things at once, and if so, suggest more focused sub-hypotheses I can test independently.',
      condition: 'always',
    },
    {
      id: 'experiment-brief',
      order: 3,
      icon: Sparkles,
      label: 'Draft an experiment brief for this',
      prompt:
        'Using this hypothesis and its assumptions, draft a structured experiment brief \u2014 include the test approach, target participants, measures, and success criteria.',
      condition: 'always',
    },
  ],
  experiment: [
    {
      id: 'review-gaps',
      order: 1,
      icon: Scroll,
      label: 'Find gaps in this experiment',
      prompt:
        'Check if the approach, measures, and success criteria are all present and flag anything that\u2019s vague, missing, or doesn\u2019t clearly test the hypothesis.',
      condition: 'always',
    },
    {
      id: 'success-criteria',
      order: 2,
      icon: Trophy,
      label: 'Strengthen my success criteria',
      prompt:
        'Review my current success criteria and rewrite them to be more specific, measurable, and directly tied to the hypothesis I\u2019m testing.',
      condition: 'always',
    },
    {
      id: 'facilitator-guide',
      order: 3,
      icon: FileText,
      label: 'Draft a facilitator guide for this experiment',
      prompt:
        'Using the approach and measures, draft a structured facilitator guide with an opening, key tasks, and closing prompts I can use to run this experiment.',
      condition: 'always',
    },
    {
      id: 'summarize',
      order: 4,
      icon: Presentation,
      label: 'Summarize this experiment for sharing',
      prompt:
        'Condense the approach, measures, and outcome of this experiment into a short summary I can paste into a doc or status update.',
      condition: 'always',
    },
  ],
};
